import { ArrowLeft, ArrowDown, ArrowUp, Minus, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useDreAuditGetById, type DreAuditKpiAggregate } from '../../hooks/useDreAudit'
import { AuditDiffTable } from './AuditDiffTable'
import { formatBRL, formatPct, pctChange } from './diffUtils'

interface AuditHistoryDetailProps {
  id: string
  onBack: () => void
}

export function AuditHistoryDetail({ id, onBack }: AuditHistoryDetailProps) {
  const query = useDreAuditGetById(id)

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 bg-brand-dark-lighter rounded animate-pulse" />
        <div className="h-32 bg-brand-dark-lighter rounded animate-pulse" />
        <div className="h-64 bg-brand-dark-lighter rounded animate-pulse" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar
        </Button>
        <div className="p-4 rounded-md bg-error/10 border border-error/30 text-sm text-error">
          Erro ao carregar snapshot: {(query.error as Error)?.message ?? 'desconhecido'}
        </div>
      </div>
    )
  }

  const snap = query.data

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar
        </Button>
        <div className="text-xs text-brand-gray">
          ID: <span className="font-mono">{snap.id.slice(0, 8)}…</span>
        </div>
      </div>

      {snap.status === 'pending' && (
        <div className="p-3 rounded-md bg-warning/10 border border-warning/30 flex items-start gap-2">
          <RefreshCw className="w-4 h-4 text-warning shrink-0 mt-0.5 animate-spin" />
          <div className="text-sm text-white">
            <p className="font-medium text-warning">Snapshot pendente</p>
            <p className="text-xs text-brand-gray mt-0.5">
              O cache da DRE ainda está sendo reprocessado. Atualize esta tela em alguns segundos.
            </p>
          </div>
        </div>
      )}

      {snap.status === 'failed' && (
        <div className="p-3 rounded-md bg-error/10 border border-error/30">
          <p className="font-medium text-error text-sm">Reprocessamento da DRE falhou</p>
          {snap.errorMessage && (
            <p className="text-xs text-brand-gray mt-1 font-mono break-all">
              {snap.errorMessage}
            </p>
          )}
        </div>
      )}

      {/* KPI cards — read pre-aggregated values from the snapshot. */}
      {snap.kpis && snap.kpis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {snap.kpis.map(kpi => (
            <KpiCard key={kpi.code} kpi={kpi} />
          ))}
        </div>
      )}

      {/* Diff sections per affected period */}
      {snap.periodChanges && snap.periodChanges.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white">
            Detalhamento por período afetado
          </h3>
          <AuditDiffTable
            periodChanges={snap.periodChanges}
            periods={snap.affectedPeriods}
          />
        </div>
      )}
    </div>
  )
}

interface KpiCardProps {
  kpi: DreAuditKpiAggregate
}

function KpiCard({ kpi }: KpiCardProps) {
  const delta = kpi.after - kpi.before
  const changed = Math.abs(delta) >= 0.005
  const Icon = !changed ? Minus : delta > 0 ? ArrowUp : ArrowDown
  const colorClass = !changed
    ? 'text-brand-gray'
    : delta > 0
      ? 'text-success'
      : 'text-error'

  return (
    <div className="p-3 bg-brand-dark-light rounded-monumenta border border-brand-dark-lighter">
      <p className="text-xs text-brand-gray uppercase tracking-wide font-semibold">{kpi.label}</p>
      <div className="flex items-baseline gap-1 mt-1">
        <Icon size={14} className={colorClass} />
        <span className={`text-base font-mono ${colorClass}`}>
          {formatBRL(delta)}
        </span>
        <span className={`text-xs font-mono ${colorClass}`}>
          {formatPct(pctChange(kpi.before, kpi.after))}
        </span>
      </div>
      <p className="text-[10px] text-brand-gray mt-1 font-mono">
        {formatBRL(kpi.before)} → {formatBRL(kpi.after)}
      </p>
    </div>
  )
}
